import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchPricingContext, type AppliedProfile } from "@/lib/api";
import { summarizePricebook } from "@/lib/pricingForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useT } from "@/app/localization";

/**
 * Charger-details "Applied Pricing Profiles": every organization-wide pricing-profile
 * assignment that can affect charging on this charger (an individual driver or a
 * pricing driver group), most specific first as the org pricing resolver prefers
 * (fetched from the backend pricing-context endpoint, never recomputed here). Uses the
 * shared summarizePricebook so idle/activation are never omitted. Manager/admin-only
 * (the endpoint enforces it).
 */
export function AppliedPricingProfiles({ unitId }: { unitId: string }) {
  const t = useT();
  const q = useQuery({
    queryKey: ["pricing-context", unitId],
    queryFn: () => fetchPricingContext(unitId),
    enabled: !!unitId,
    retry: false,
  });

  // A driver / unrelated manager gets 403/404 — silently render nothing (no leak).
  if (q.isError) return null;
  if (q.isLoading) return <div className="text-sm opacity-60">{t("operator.pricing.applied_loading")}</div>;

  const ctx = q.data!;
  const profiles = ctx.applied_profiles ?? [];

  return (
    <div className="border-t pt-4 space-y-3" data-testid="applied-pricing-profiles">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{t("operator.pricing.applied_title")}</h3>
        {ctx.organization_id != null && (
          <Button asChild size="xs" variant="outline">
            <Link to="/pricing-profiles" data-testid="manage-pricing-profiles">{t("operator.pricing.applied_manage")}</Link>
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {/* precedence_explanation is server English (Phase 6/7 owner), shown verbatim. */}
        {ctx.precedence_explanation} {t("operator.pricing.applied_eligibility")}
      </p>

      {profiles.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="applied-profiles-empty">
          {t("operator.pricing.applied_empty")}
        </div>
      ) : (
        <section className="space-y-2" data-testid="applied-active">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("operator.pricing.applied_active")}
          </h4>
          {profiles.map((p) => <ProfileCard key={p.assignment_id} p={p} />)}
        </section>
      )}
    </div>
  );
}

function ProfileCard({ p }: { p: AppliedProfile }) {
  const t = useT();
  const audience = p.audience_type === "user"
    ? t("operator.pricing.assignment_audience_driver", { name: p.audience_name })
    : t("operator.pricing.assignment_audience_group", { name: p.audience_name });
  return (
    <div className="rounded-md border p-3 text-sm" data-testid={`applied-profile-${p.profile_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{p.profile_name}</span>
        <Badge variant="success" className="capitalize">{t("operator.pricing.applied_status", { status: p.status })}</Badge>
      </div>
      <div className="mt-1" data-testid={`applied-summary-${p.profile_id}`}>
        {summarizePricebook(p.pricebook, t)}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {/* scope is server English (Phase 6/7 owner), shown verbatim. */}
        {t("operator.pricing.applied_details", {
          audience, scope: p.scope, period: t("operator.pricing.applied_period_always"),
        })}
      </div>
    </div>
  );
}
